/**
 * The sections of Settings (plan §2.14, boards D16–D19b, D40–D44, D63): their order and group in
 * the section column (desktop) and the grouped list (phone), what each one is for, where it
 * applies, and the words its search finds.
 */

export type SettingsSection =
  | 'account'
  | 'general'
  | 'agents'
  | 'personas'
  | 'models'
  | 'voice'
  | 'notifications'
  | 'worktrees'
  | 'computer-use'
  | 'keyboard-shortcuts'
  | 'integrations'
  | 'plugins'
  | 'connections';

/** The groups of the column: personal sections, the organization's, then plugins. */
type SettingsGroupId = 'personal' | 'organization' | 'plugins';

export type SettingsSectionInfo = {
  id: SettingsSection;
  title: string;
  /** The line under the page title. */
  description: string;
  group: SettingsGroupId;
  /** Only on a computer: the phone app hides it (plan Appendix B, réglages 3). */
  desktopOnly?: boolean;
  /** Only where agents run on this machine (`crowdex serve`, the Desktop). */
  localOnly?: boolean;
  /** What else its search finds: the settings it holds. */
  keywords: readonly string[];
};

export const SETTINGS_SECTIONS: readonly SettingsSectionInfo[] = [
  {
    id: 'account',
    title: 'Account',
    description: 'Your profile, Crowdex Cloud and the devices you are signed in on.',
    group: 'personal',
    keywords: [
      'profile',
      'display name',
      'email',
      'photo',
      'organization',
      'linked computers',
      'devices',
      'sign out',
      'cloud',
    ],
  },
  {
    id: 'general',
    title: 'General',
    description: 'Changes apply right away.',
    group: 'personal',
    keywords: [
      'appearance',
      'theme',
      'dark',
      'light',
      'accent',
      'colour',
      'color',
      'compact',
      'auto-approve',
      'predictions',
      'pull requests',
      'drafts',
      'editor',
      'open at login',
      'picture-in-picture',
      'language',
      'updates',
      'desktop app',
      'download',
    ],
  },
  {
    id: 'agents',
    title: 'Agents',
    description:
      'The default agent starts new conversations. Each conversation can still pick another one from its + menu.',
    group: 'personal',
    keywords: [
      'default agent',
      'make default',
      'runtimes',
      'claude code',
      'codex',
      'models',
      'connect',
      'acp',
      'launch command',
    ],
  },
  {
    id: 'personas',
    title: 'Personas',
    description:
      'Your agent profiles: a name, instructions and what each may use. Assistants use them as personas.',
    group: 'personal',
    keywords: [
      'agent profiles',
      'saved agents',
      'profiles',
      'new persona',
      'instructions',
      'avatar',
    ],
  },
  {
    id: 'models',
    title: 'Models',
    description: 'What Crowpus offers, and the default for each thing a model makes.',
    group: 'personal',
    keywords: [
      'models',
      'default model',
      'text',
      'images',
      'narration',
      'transcription',
      'crowpus',
      'mistral',
      'elevenlabs',
      'providers',
    ],
  },
  {
    id: 'voice',
    title: 'Voice',
    description: 'Voice calls with your assistant, and dictation.',
    group: 'personal',
    keywords: [
      'live',
      'call',
      'engine',
      'voice',
      'avatar',
      'dictation',
      'microphone',
      'speech',
      'language',
    ],
  },
  {
    id: 'notifications',
    title: 'Notifications',
    description:
      'What you are told of, for each type of event. The same choices apply in Crowdex Desktop and in Crowdex Cloud.',
    group: 'personal',
    keywords: [
      'events',
      'toast',
      'system',
      'call',
      'list',
      'do not disturb',
      'push',
      'test notification',
    ],
  },
  {
    id: 'worktrees',
    title: 'Worktrees',
    description:
      'Each conversation gets its own branch and working copy, so several agents can work on one repository at once.',
    group: 'personal',
    keywords: ['folder', 'disk', 'cleanup', 'merged branches', 'keep'],
  },
  {
    id: 'computer-use',
    title: 'Computer use',
    description: 'Only on a computer: the mobile app hides it.',
    group: 'personal',
    desktopOnly: true,
    localOnly: true,
    keywords: [
      'cua driver',
      'mac',
      'apps',
      'screen recording',
      'accessibility',
      'ask before',
      'passwords',
      'buying',
    ],
  },
  {
    id: 'keyboard-shortcuts',
    title: 'Keyboard shortcuts',
    description: 'Click a shortcut to change it.',
    group: 'personal',
    desktopOnly: true,
    keywords: ['keys', 'keyboard', 'hotkeys', 'new task', 'sidebar', 'panel', 'approve'],
  },
  {
    id: 'integrations',
    title: 'Integrations',
    description: 'Admins only. Members see these settings locked.',
    group: 'organization',
    keywords: [
      'github',
      'gitlab',
      'forges',
      'repositories',
      'marketplaces',
      'plugin sources',
      'catalog',
    ],
  },
  {
    id: 'plugins',
    title: 'Plugins',
    description: 'Apps, editors and services',
    group: 'plugins',
    keywords: [
      'services',
      'editors',
      'connectors',
      'skills',
      'mcp',
      'apps',
      'agents',
      'marketplaces',
      'install',
      'installed',
      'custom',
      'browse',
    ],
  },
  {
    id: 'connections',
    title: 'Connections',
    description: 'The accounts of your services that plugins and agents act with.',
    group: 'plugins',
    keywords: [
      'accounts',
      'reconnect',
      'sign in',
      'oauth',
      'api key',
      'token',
      'google',
      'github',
      'gitlab',
      'slack',
    ],
  },
];

/** Settings › Models: the models Crowpus offers, and the defaults. */
export const MODELS_PATH = '/settings?section=models';

/** Where the column is shown: phones hide desktop-only sections, other servers local ones. */
export function availableSections(where: { phone: boolean; local: boolean }) {
  return SETTINGS_SECTIONS.filter(
    (section) => !(where.phone && section.desktopOnly) && !(section.localOnly && !where.local),
  );
}

export function sectionInfo(id: SettingsSection): SettingsSectionInfo {
  // Every id is listed above.
  return SETTINGS_SECTIONS.find((section) => section.id === id) as SettingsSectionInfo;
}

const OLD_SECTION_IDS: Record<string, SettingsSection> = {
  'organization-integrations': 'integrations',
  'organization-connections': 'integrations',
  shortcuts: 'keyboard-shortcuts',
};

/** A persona's page in Settings › Personas. */
export function personaPath(id: string): string {
  return `/settings?section=personas&profile=${encodeURIComponent(id)}`;
}

/** The section a `?section=` names, or `null` (the phone's list; the desktop opens General). */
export function parseSettingsSection(value: string | null): SettingsSection | null {
  if (value === null) return null;
  if (OLD_SECTION_IDS[value]) return OLD_SECTION_IDS[value];
  return SETTINGS_SECTIONS.some((section) => section.id === value)
    ? (value as SettingsSection)
    : null;
}

/** A phone screen's title for an app path: a Settings sub-page's section, else `null`. */
export function settingsScreenTitle(path: string): string | null {
  const url = new URL(path, 'https://crowdex.invalid');
  if (url.pathname !== '/settings') return null;
  const section = parseSettingsSection(url.searchParams.get('section'));
  return section === null ? null : sectionInfo(section).title;
}

export function settingsSectionPath(section: SettingsSection): string {
  return `/settings?section=${section}`;
}

function matches(query: string, words: readonly string[]): boolean {
  return words.some((word) => word.toLowerCase().includes(query));
}

/**
 * What "Search settings" keeps: the sections and destinations whose title or settings hold the
 * query (case-insensitive); everything for an empty query.
 */
export function searchSettings<T extends { title: string; keywords: readonly string[] }>(
  entries: readonly T[],
  query: string,
): T[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return [...entries];
  return entries.filter((entry) => matches(needle, [entry.title, ...entry.keywords]));
}
